import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, Book, Loader2, Volume2, RefreshCw, Heart, Star,
  Castle, Rocket, TreeDeciduous, Fish, Crown
} from 'lucide-react';
import { speak as _speakTTS, stopSpeaking as _stopTTS } from '@/utils/speak';

const AIStoryGenerator = ({ childName, childAge, onStoryComplete }) => {
  const { api } = useAuth();
  const [theme, setTheme] = useState('');
  const [mainCharacter, setMainCharacter] = useState(childName || '');
  const [setting, setSetting] = useState('');
  const [story, setStory] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [speaking, setSpeaking] = useState(false);

  const themes = [
    { value: 'adventure', label: 'Adventure', icon: Rocket },
    { value: 'friendship', label: 'Friendship', icon: Heart },
    { value: 'nature', label: 'Nature & Animals', icon: TreeDeciduous },
    { value: 'fantasy', label: 'Fantasy & Magic', icon: Castle },
    { value: 'underwater', label: 'Underwater', icon: Fish },
    { value: 'space', label: 'Space Exploration', icon: Star },
    { value: 'princess', label: 'Princess & Kingdom', icon: Crown },
  ];

  const settings = [
    { value: 'enchanted_forest', label: 'Enchanted Forest' },
    { value: 'magical_kingdom', label: 'Magical Kingdom' },
    { value: 'outer_space', label: 'Outer Space' },
    { value: 'underwater_world', label: 'Underwater World' },
    { value: 'cozy_village', label: 'Cozy Village' },
    { value: 'ancient_castle', label: 'Ancient Castle' },
    { value: 'sunny_beach', label: 'Sunny Beach' },
  ];

  const generateStory = async () => {
    if (!theme || !mainCharacter) {
      toast.error('Please select a theme and enter a character name');
      return;
    }

    setGenerating(true);
    setStory(null);

    try {
      const response = await api.post('/stories/generate', null, {
        params: {
          theme,
          main_character: mainCharacter,
          setting: setting || 'magical place',
          age_group: childAge < 5 ? 'toddler' : childAge < 8 ? 'early_elementary' : 'elementary'
        }
      });
      
      setStory(response.data);
      toast.success('Story created!');
      
      if (onStoryComplete) {
        onStoryComplete(response.data);
      }
    } catch (error) {
      console.error('Story generation error:', error);
      toast.error('Failed to generate story. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const speakStory = () => {
    if (!story) return;

    if (speaking) {
      _stopTTS();
      setSpeaking(false);
      return;
    }

    setSpeaking(true);
    _speakTTS(story.content, 0.9, () => setSpeaking(false));
  };

  const formatStory = (content) => {
    if (!content) return null;
    
    return content.split('\n\n').map((paragraph, index) => (
      <motion.p
        key={index}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1 }}
        className="mb-4 text-lg leading-relaxed text-slate-700"
      >
        {paragraph}
      </motion.p>
    ));
  };

  return (
    <div className="space-y-6" data-testid="ai-story-generator">
      {/* Story Creator Form */}
      {!story && (
        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-purple-800">
              <Sparkles className="w-6 h-6 text-purple-500" />
              Create Your Story
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Theme Selection */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Choose a Theme</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {themes.map(t => {
                  const Icon = t.icon;
                  return (
                    <Button
                      key={t.value}
                      variant={theme === t.value ? 'default' : 'outline'}
                      onClick={() => setTheme(t.value)}
                      className={`h-auto py-3 flex-col gap-1 ${
                        theme === t.value ? 'bg-purple-500 hover:bg-purple-600' : ''
                      }`}
                      data-testid={`theme-${t.value}`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-xs">{t.label}</span>
                    </Button>
                  );
                })}
              </div>
            </div>

            {/* Character Name */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Main Character's Name</label>
              <Input
                value={mainCharacter}
                onChange={(e) => setMainCharacter(e.target.value)}
                placeholder="Enter the hero's name..."
                className="text-lg"
                data-testid="character-name-input"
              />
            </div>

            {/* Setting */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Story Setting (optional)</label>
              <Select value={setting} onValueChange={setSetting}>
                <SelectTrigger data-testid="setting-select">
                  <SelectValue placeholder="Choose where the story happens..." />
                </SelectTrigger>
                <SelectContent>
                  {settings.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Generate Button */}
            <Button
              onClick={generateStory}
              disabled={generating || !theme || !mainCharacter}
              className="w-full h-12 text-lg bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              data-testid="generate-story-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Creating your story...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Generate Story
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Generated Story */}
      <AnimatePresence>
        {story && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <CardTitle className="flex items-center gap-2 text-amber-800">
                    <Book className="w-6 h-6 text-amber-500" />
                    {story.title}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={speakStory}
                      className={speaking ? 'text-amber-600 bg-amber-100' : 'text-amber-600'}
                      data-testid="speak-story-btn"
                    >
                      <Volume2 className="w-5 h-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setStory(null)}
                      className="text-amber-600"
                      data-testid="new-story-btn"
                    >
                      <RefreshCw className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
                {story.subtitle && (
                  <p className="text-amber-600 text-sm mt-1">{story.subtitle}</p>
                )}
              </CardHeader>
              <CardContent>
                {/* Story Image Placeholder */}
                <div className="w-full h-48 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl mb-6 flex items-center justify-center">
                  <div className="text-center">
                    <Star className="w-12 h-12 text-amber-400 mx-auto mb-2" />
                    <p className="text-amber-600 text-sm">Story illustration</p>
                  </div>
                </div>

                {/* Story Content */}
                <div className="prose prose-lg max-w-none">
                  {formatStory(story.content)}
                </div>

                {/* Moral/Lesson */}
                {story.moral && (
                  <div className="mt-6 p-4 bg-amber-100 rounded-xl border border-amber-200">
                    <p className="text-amber-800 font-medium">
                      <span className="text-amber-500">✨ Lesson:</span> {story.moral}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="mt-6 flex flex-wrap gap-3 justify-center">
                  <Button
                    variant="outline"
                    onClick={() => setStory(null)}
                    className="gap-2"
                  >
                    <RefreshCw className="w-4 h-4" /> Create Another Story
                  </Button>
                  <Button
                    variant="outline"
                    onClick={speakStory}
                    className="gap-2"
                  >
                    <Volume2 className="w-4 h-4" /> {speaking ? 'Stop Reading' : 'Read Aloud'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AIStoryGenerator;
